// Screens a household can turn on or off (Admin → Screens & schedule). A
// screen that's off leaves both navigations and the schedule, and nothing
// works for it in the background: no hourly job on the Worker, no refreshing
// of its data on the wall, and its API answers "turned off". Widget layouts
// can be turned off the same way, by layout id.

export type OwnScreenId = "today" | "atlas" | "resorts" | "drive" | "lookout" | "whiteboard";

export interface OwnScreenDef {
  id: OwnScreenId;
  name: string;
  /** An icon name from src/ui/Icon.tsx. */
  icon: string;
  blurb: string;
  /** What stops when it's off, in plain words for the admin page. */
  stops: string;
  /** First parts of the query keys only this screen uses; the wall drops and stops refreshing them. */
  queryKeys: string[];
}

/** The screens that aren't widget layouts, in navigation order. */
export const OWN_SCREENS: OwnScreenDef[] = [
  {
    id: "today",
    name: "Today",
    icon: "sparkle",
    blurb: "The Almanac’s ranked view of the day: sky, weather windows, holidays and the family’s day.",
    stops: "Stops the hourly job that records each day’s weather at home for the Almanac’s records.",
    queryKeys: ["almanac"],
  },
  {
    id: "atlas",
    name: "Living Atlas",
    icon: "globe",
    blurb: "Live maps of the planet: storms, fires, aurora, rivers, snow and more.",
    stops: "Stops the ambient map rotation and the atlas layers refreshing on the wall.",
    queryKeys: ["atlas"],
  },
  {
    id: "resorts",
    name: "Resorts",
    icon: "ski",
    blurb: "One ski area at a time: snow, the forecast, webcams, the trail map, roads and traffic, avalanche danger and news.",
    stops: "Stops the resort forecasts, webcams and news refreshing on the wall.",
    queryKeys: ["resorts"],
  },
  {
    id: "drive",
    name: "Drive",
    icon: "car",
    blurb: "Where the family’s headed, the drive from home and Google’s directions map.",
    stops: "Stops drive times refreshing on the wall (Directions from a widget still work).",
    queryKeys: [],
  },
  {
    id: "lookout",
    name: "Lookout",
    icon: "camera",
    blurb: "Webcams near home, at your mountains and around the world.",
    stops: "Stops the camera tour and the camera lists refreshing on the wall.",
    queryKeys: ["webcams"],
  },
  {
    id: "whiteboard",
    name: "Whiteboard",
    icon: "board",
    blurb: "A board to draw on, one per day.",
    stops: "Stops the board syncing between screens.",
    queryKeys: ["whiteboard"],
  },
];

export const ownScreen = (id: string) => OWN_SCREENS.find((s) => s.id === id);

export interface ScreenSettings {
  /** Own screen ids (except the Living Atlas, which keeps its own switch) and layout ids that are turned off. */
  off: string[];
}

export const DEFAULT_SCREENS: ScreenSettings = { off: [] };

const MAX_OFF = 60;
const ID_RE = /^[A-Za-z0-9_-]{1,80}$/;

export function validateScreens(raw: unknown): ScreenSettings {
  const off = (raw as { off?: unknown } | null)?.off;
  if (!Array.isArray(off) || off.length > MAX_OFF || !off.every((id) => typeof id === "string" && ID_RE.test(id))) throw new Error("Screens to turn off must be a list of screen ids");
  return { off: [...new Set(off as string[])] };
}

/** Whether a screen (own screen id or layout id) is on. */
export const screenOn = (s: ScreenSettings | undefined, id: string) => !(s?.off ?? []).includes(id);
